import { useEffect, useState } from "react";
import { Alert, Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import LoadingIndicator from '../components/LoadingIndicator';
import LoadError from '../components/LoadError';
import dayjs from "dayjs";
import api from "../api/axios";
import { useBusinessContext, businessError } from "../BusinessContext";
import PageHeader from "../components/PageHeader";
import RecordLink from "../components/RecordLink";

const groups = [
  ["rehwild", "Rehwild"], ["rotwild", "Rotwild"],
  ["schwarzwild", "Schwarzwild"], ["niederwild", "Niederwild"],
  ["fuchs", "Füchse"], ["dachs", "Dachse"],
];
const euro = (value) => new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(Number(value || 0));
const integer = (value) => new Intl.NumberFormat("de-DE", { maximumFractionDigits: 0 }).format(Number(value || 0));

export default function Dashboard() {
  const { ready, withContext } = useBusinessContext();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);
  const mengenreport = data?.mengenreport ?? [];
  const unknownQuantities = mengenreport.reduce((sum, row) => sum + (row.anzahl_unbekannt || 0), 0);

  useEffect(() => {
    if (!ready) return;
    let active = true;
    setLoading(true);
    setData(null);
    setError("");
    api.get(withContext("/dashboard"))
      .then((response) => { if (active) setData(response.data); })
      .catch(error => { if (active) setError(businessError(error, "Dashboard konnte nicht geladen werden.")); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [ready, withContext, attempt]);

  return <Box>
    <PageHeader title="Dashboard" />
    {loading && <LoadingIndicator label="Dashboard wird geladen" />}
    {error && <LoadError onRetry={() => setAttempt(value => value + 1)}>{error}</LoadError>}
    {!loading && data && <>
      <Typography variant="h6" component="h2" sx={{ mb: 1 }}>Erlegtes Wild</Typography>
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(155px, 1fr))", gap: 1.25, mb: 2 }}>
        {groups.map(([key, label]) => <Paper key={key} data-testid={`wild-${key}`} elevation={1} sx={{ p: 1.5, minWidth: 0 }}>
          <Typography variant="body2" color="text.secondary">{label}</Typography>
          <Typography variant="h4" component="div">{data.wild[key].anzahl}</Typography>
          {data.wild[key].ertrag !== undefined && <Typography variant="body2">Ertrag: {euro(data.wild[key].ertrag)}</Typography>}
        </Paper>)}
      </Box>
      <Typography variant="h6" component="h2" sx={{ mb: 1 }}>Mengenreport Wild</Typography>
      {unknownQuantities > 0 && <Alert severity="info" sx={{ mb: 1 }}>
        Bei {integer(unknownQuantities)} Streckeneinträgen fehlt die Anzahl. Diese sind in der Anzahl nicht mitgezählt; ihr Erlös ist enthalten.
      </Alert>}
      <TableContainer component={Paper} sx={{ mb: 2 }}>
        <Table size="small" aria-label="Mengenreport Wild" sx={{ "& td, & th": { whiteSpace: "nowrap" } }}>
          <TableHead><TableRow>
            <TableCell>Wildart</TableCell><TableCell align="right">Anzahl</TableCell><TableCell align="right">Erlös</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {mengenreport.length === 0 ? <TableRow>
              <TableCell colSpan={3} align="center">Keine Strecke im ausgewählten Jagdjahr.</TableCell>
            </TableRow> : mengenreport.map((row) => <TableRow key={row.wildart}>
              <TableCell>{row.wildart}</TableCell>
              <TableCell align="right">{integer(row.anzahl)}</TableCell>
              <TableCell align="right">{euro(row.erloes)}</TableCell>
            </TableRow>)}
          </TableBody>
        </Table>
      </TableContainer>
      <Typography variant="h6" component="h2" sx={{ mb: 1 }}>Unbezahlte Verkäufe</Typography>
      <Box sx={{ display: "flex", gap: 2, mb: 1, flexWrap: "wrap" }}>
        <Typography>Anzahl: <strong>{data.unbezahlt.anzahl}</strong></Typography>
        <Typography>Offener Betrag: <strong>{euro(data.unbezahlt.betrag)}</strong></Typography>
      </Box>
      {data.unbezahlt.verkaeufe.length === 0 ? <Typography>Keine unbezahlten Verkäufe</Typography> :
        <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
          <Table size="small" stickyHeader sx={{ "& td, & th": { whiteSpace: "nowrap" } }}>
            <TableHead><TableRow><TableCell>Kunde</TableCell><TableCell>Datum</TableCell><TableCell>Wild / Strecke</TableCell><TableCell align="right">Betrag</TableCell></TableRow></TableHead>
            <TableBody>{data.unbezahlt.verkaeufe.map((sale) => <TableRow key={sale.id}>
              <TableCell>{sale.kunde || "–"}</TableCell>
              <TableCell>{sale.datum ? dayjs(sale.datum).format("DD.MM.YYYY") : "–"}</TableCell>
              <TableCell><RecordLink page="strecke" id={sale.strecke_id} betriebsjahrId={data.betriebsjahr_id}>
                {sale.altersklasse || "–"} ({sale.jaeger || "–"}, {sale.erlegedatum ? dayjs(sale.erlegedatum).format("DD.MM.YYYY") : "–"})
              </RecordLink></TableCell>
              <TableCell align="right">{euro(sale.betrag)}</TableCell>
            </TableRow>)}</TableBody>
          </Table>
        </TableContainer>}
    </>}
  </Box>;
}
